import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { useWeekStartDay } from 'modules/common/hooks/useWeekStartDay';
import { SearchCriteria } from 'modules/notes/common';
import { getCurrentWeekDateRange } from './hooks/useNoteInsightsDateRange';
import { NoteInsightsViewOptions } from './NoteInsightsContextProvider';

type Period = {
  endDate: string;
  startDate: string;
};

type SupervisorInsightContextValue = {
  currentPeriod: Period | null;
  setcurrentPeriod: (period: Period) => void;
  setSelectedViewOptionSupervisor: (
    selectedViewOptionSupervisor: NoteInsightsViewOptions
  ) => void;
  selectedViewOptionSupervisor: NoteInsightsViewOptions;
  searchLabelType: string;
  setSearchLabelType: React.Dispatch<React.SetStateAction<string>>;
  searchCriteriaInsights: SearchCriteria;
  setSearchCriteriaInsights: React.Dispatch<
    React.SetStateAction<SearchCriteria>
  >;
};

export const SupervisorInsightContext = createContext<SupervisorInsightContextValue | null>(
  null
);

export const useSupervisorInsightContext = (): SupervisorInsightContextValue => {
  const value = useContext(SupervisorInsightContext);
  if (!value) {
    throw Error('should be called in SupervisorInsightContextProvider scope');
  }
  return value;
};

export type SupervisorInsightContextProviderType = {
  children?: React.ReactNode;
};

const SupervisorInsightContextProvider: React.FC<SupervisorInsightContextProviderType> = ({
  children
}: SupervisorInsightContextProviderType) => {
  const weekStart = useWeekStartDay();
  const { startDate, endDate } = getCurrentWeekDateRange(weekStart);
  const [searchLabelType, setSearchLabelType] = useState<string>('');
  const [
    searchCriteriaInsights,
    setSearchCriteriaInsights
  ] = useState<SearchCriteria>({
    keywords: [],
    criterions: {}
  });

  const [currentPeriod, setcurrentPeriod] = useState<Period | null>({
    startDate: startDate,
    endDate: endDate
  });

  const [selectedViewOptionSupervisor, setSelectedOptionSupervisor] = useState(
    NoteInsightsViewOptions.THIS_WEEK
  );

  const setSelectedViewOptionSupervisor = useCallback(
    (selectedViewOptionSupervisor: NoteInsightsViewOptions) => {
      setSelectedOptionSupervisor(selectedViewOptionSupervisor);
    },
    []
  );

  const value = useMemo(
    () => ({
      currentPeriod,
      setcurrentPeriod,
      selectedViewOptionSupervisor,
      setSelectedViewOptionSupervisor: setSelectedViewOptionSupervisor,
      searchLabelType,
      setSearchLabelType,
      searchCriteriaInsights,
      setSearchCriteriaInsights
    }),
    [
      currentPeriod,
      selectedViewOptionSupervisor,
      setSelectedViewOptionSupervisor,
      searchLabelType,
      searchCriteriaInsights
    ]
  );
  return (
    <SupervisorInsightContext.Provider value={value}>
      {children}
    </SupervisorInsightContext.Provider>
  );
};

export default SupervisorInsightContextProvider;
